// @flow
import React, { Fragment } from 'react';
import { EntityCard } from '@finect/tabular-components/EntityCard';
import { DateLabel } from '@finect/tabular-components/DateLabel';
import { AppLink } from '@finect/tabular-core/AppLink';
import { CollapsibleContentSmall } from '@finect/tabular-components/Collapsible';
import { decodeHtmlEntities } from '@finect/front-utils/html';
import { GroupProfileHeading } from '../../../../components/Profiles/GroupProfileHeading';
import {
  ArticleHeaderContent,
  ArticleHeaderInner,
  ArticleMeta,
  ArticleUserAuthor,
  ArticleUserEdit
} from '../../../../components';
import { Title2 } from '../../../../components/ContentListCard/components/Headings';
import { getArticleEditionPath } from '../../../../utils/articles';

type Props = {
  me: Object,
  isFirst: boolean,
  article: Object,
  onProfileFollow: Function,
  profileSubscriptions: Array<string>,
  children: any
};

export const ArticleHeader = (props: Props) => {
  const { me, isFirst = false, article, profileSubscriptions, onProfileFollow } = props;
  const { author } = article;

  const revisionAuthor = article.revision.user;
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 600 : false;

  const title = decodeHtmlEntities(article.title);

  const isUpdated =
    new Date(article.revision.created).toISOString()
      .split('T', 1)[0] !==
    new Date(article.revision.updated).toISOString()
      .split('T', 1)[0];

  const isVideo = ['video'].includes(article.type);

  if (author.subtype === 'company') {
    return (
      <>
        <GroupProfileHeading
          profile={author}
          user={me}
          subscriptionsProfiles={profileSubscriptions}
          onFollow={onProfileFollow}
          withLink={true}
          withDescription={false}
          withBorder={true}
        />

        <ArticleHeaderContent topMargin>
          <ArticleHeaderInner>
            <Title2 as={isFirst ? 'h1' : 'h2'}>
              {isVideo && <span className="extra">Vídeo - </span>}
              {title}
            </Title2>
            <ArticleMeta>
              <span>
                <DateLabel date={new Date(article.revision.created)} format="LLL" noTimeTag={!isFirst} />
                {isUpdated && (
                  <span>
                    {' · Actualizado '}
                    <DateLabel date={new Date(article.revision.updated)} format="LLL" noTimeTag={!isFirst} />
                  </span>
                )}
              </span>
            </ArticleMeta>

            {props.children}
          </ArticleHeaderInner>
        </ArticleHeaderContent>
      </>
    );
  }

  const [authorFirstOrg] = (revisionAuthor || author).organizations || [];

  return (
    <ArticleHeaderContent>
      <ArticleHeaderInner>
        <Title2 as={isFirst ? 'h1' : 'h2'}>
          {isVideo && <span className="extra">Vídeo - </span>}
          {title}
        </Title2>

        <ArticleMeta noBottom={isMobile}>
          {revisionAuthor && revisionAuthor.displayName ? (
            <CollapsibleContentSmall
              collapsed={true}
              chevronSize={isMobile ? '14' : '18'}
              titleAlign={isMobile ? 'center' : 'left'}
              title={
                <Fragment>
                  Editado por{' '}
                  <EntityCard
                    type="profile"
                    entity={{
                      ...revisionAuthor,
                      name: revisionAuthor.displayName,
                      icon: revisionAuthor.image
                    }}
                  >
                    {revisionAuthor.features?.includes('profile') ? (
                      <ArticleUserAuthor
                        noBar={isMobile}
                        href={revisionAuthor.web}
                        title={`Ver perfil público del usuario ${revisionAuthor.displayName}`}
                      >
                        {revisionAuthor.displayName}
                      </ArticleUserAuthor>
                    ) : (
                      <ArticleUserAuthor noBar={isMobile} as="span">
                        {revisionAuthor.displayName}
                      </ArticleUserAuthor>
                    )}
                  </EntityCard>
                </Fragment>
              }
            >
              Publicado por{' '}
              <EntityCard
                type="profile"
                entity={{
                  ...author,
                  name: author.displayName,
                  icon: author.image
                }}
              >
                {author.features.includes('profile') ? (
                  <ArticleUserAuthor
                    noBar
                    href={author.link}
                    title={`Ver perfil público del usuario ${author.displayName}`}
                  >
                    {author.displayName}
                  </ArticleUserAuthor>
                ) : (
                  <ArticleUserAuthor noBar as="span">
                    {author.displayName}
                  </ArticleUserAuthor>
                )}
              </EntityCard>
            </CollapsibleContentSmall>
          ) : (
            <EntityCard
              type="profile"
              entity={{
                ...author,
                name: author.displayName,
                icon: author.image
              }}
            >
              {author.features.includes('profile') ? (
                <ArticleUserAuthor
                  noBar={isMobile}
                  href={author.link}
                  title={`Ver perfil público del usuario ${author.displayName}`}
                >
                  {author.displayName}
                </ArticleUserAuthor>
              ) : (
                <ArticleUserAuthor noBar={isMobile} as="span">
                  {author.displayName}
                </ArticleUserAuthor>
              )}
            </EntityCard>
          )}

          {!isMobile && (
            <>
              <span>
                {isUpdated && <span>{' Actualizado el '} </span>}
                <DateLabel date={new Date(article.revision.updated)} format="LLL" noTimeTag={!isFirst} />
              </span>

              {me && me.isLoggedIn && article.author.id === me.id ? (
                <ArticleUserEdit>
                  <AppLink href={getArticleEditionPath(article)} rel="nofollow">editar artículo</AppLink>
                </ArticleUserEdit>
              ) : null}
            </>
          )}

          {authorFirstOrg && authorFirstOrg.displayName ? (
            <span className="author">{authorFirstOrg.displayName}</span>
          ) : null}
        </ArticleMeta>

        {isMobile ? (
          <ArticleMeta>
            <span>
              {isUpdated && <span>{' Actualizado el '} </span>}
              <DateLabel date={new Date(article.revision.updated)} format="LLL" noTimeTag={!isFirst} />
            </span>

            {me && me.isLoggedIn && article.author.id === me.id ? (
              <ArticleUserEdit>
                <AppLink href={getArticleEditionPath(article)} rel="nofollow">editar artículo</AppLink>
              </ArticleUserEdit>
            ) : null}
          </ArticleMeta>
        ) : null}

        {props.children}
      </ArticleHeaderInner>
    </ArticleHeaderContent>
  );
};
